import { effect, inject, Injectable, untracked } from '@angular/core';
import { StompService } from 'app/shared/services/stomp.service';
import { GlobalStore } from 'app/store/global.store';

/**
 * Opens the shared STOMP socket only while a signed-in session exists and closes it on sign-out; a user
 * change finishes closing the old socket before the next user's opens. A token fetch the previous user left
 * pending is not cancelled, so if it resolves after that the socket opens with the previous user's token.
 */
@Injectable({
  providedIn: 'root',
})
export class StompSessionService {
  private readonly globalStore = inject(GlobalStore);
  private readonly stompService = inject(StompService);

  private activeUserId: string | null = null;
  private transitions: Promise<void> = Promise.resolve();

  constructor() {
    effect(() => {
      const session = this.globalStore.currentSession();
      const userId = session?.accessToken ? (session.userId ?? null) : null;
      untracked(() => this.enqueue(userId));
    });
  }

  private enqueue(userId: string | null): void {
    // Without the catch, one rejected transition would skip every later one.
    this.transitions = this.transitions.then(() => this.switchTo(userId)).catch(() => undefined);
  }

  private async switchTo(userId: string | null): Promise<void> {
    if (userId === this.activeUserId) {
      return;
    }
    const previousUserId = this.activeUserId;
    this.activeUserId = userId;
    if (previousUserId) {
      // Forced: a normal deactivate waits for a DISCONNECT receipt that a dead connection never sends.
      await this.stompService.deactivate({ force: true });
      // A rejected token fetch keeps the last header, so it must never still hold the previous user's token.
      this.stompService.configure({ connectHeaders: {} });
    }
    if (userId) {
      this.stompService.activate();
    }
  }
}
